import { ToggleMic, controlServiceImpl } from "@lvt/streaming";
import { unwrap } from "@lvt/web-data";
import {
    Button,
    CircularProgress,
    Grid,
    makeStyles,
    Fab,
    Card,
    CardHeader,
    Popper,
    Fade,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Typography,
    IconButton,
    Divider,
    Tooltip,

} from "@material-ui/core";
import { Mic, MicOff, VolumeOff, VolumeUp, Speaker, ArrowForwardIos, MusicNote, Close, KeyboardArrowLeft, Phone, Smartphone } from "@material-ui/icons";
import clsx from "clsx";
import { useState, useCallback, useEffect } from "react";

import client from "@/api/client";
import { useLazySpeakerTalkdownStartQuery } from "@/api/speakerTalkdown/speakerTalkdownApi";
import useActionButtonStyles from "@/common/components/streaming/ActionButtons/ActionButton.styles";
import QuickSounds from "@/common/components/streaming/ActionButtons/QuickSoundsButton/QuickSounds";
import { useDispatch, useSelector } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";
import { Config, CallControls, startTwilioCall } from "@/utilities/twilio";
import { OpenButton } from "@/utilities/types/streaming";


const useStyles = makeStyles((theme) => ({
    speakerTalkdownButton: {
        color: theme.palette.primary.contrastText,
    },
    speakerTalkdownIconButton: {
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.light,
        minWidth: 0,
        padding: 3,
    },
    redButton: {
        backgroundColor: theme.palette.error.main,
        color: "#ffffff",
        "&:hover": {
            backgroundColor: theme.palette.error.dark,
        },
    },
    close: {
        position: "absolute",
        top: 8,
        right: 8,
        color: theme.palette.primary.contrastText,
    },
    back: {
        color: theme.palette.primary.contrastText,
    },
    divider: {
        backgroundColor: theme.palette.primary.light,
    },
    menu: {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.contrastText,
        borderRadius: 8,
    },
    popper: {
        zIndex: theme.zIndex.modal,
        marginBottom: 16,
        width: 300,
        marginRight: "0.625rem",
    },
    cardContent: {
        padding: 0,
    },
    icon: {
        fill: theme.palette.primary.contrastText,
    },
    headerWithArrow: {
        paddingLeft: 0,
    },
    buttonContainer: {
        padding: "16px",
    },
    phoneNumberIcon: {
        minWidth: 30,
    },
}));

interface SpeakerButtonProps {
    allowSpeakerTalkdown: boolean;
    liveUnitId: number;
    menuAnchor: HTMLElement | null;
    handleMenu: (button: OpenButton) => void;
    open: boolean;
}

enum SpeakerState {
    Menu,
    QuickSounds,
    TalkDown
}

const SpeakerButtons: React.FC<SpeakerButtonProps> = ({ allowSpeakerTalkdown, liveUnitId, menuAnchor, handleMenu, open }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [isCallConnected, setIsCallConnected] = useState(false);
    const [isCallConnecting, setIsCallConnecting] = useState(false);
    const [selfMuted, setSelfMuted] = useState(false);
    const [unitMuted, setUnitMuted] = useState(true);
    const [callControls, setCallControls] = useState<CallControls | null>(null); //todo: Make these reset when call ends
    const [selectedSpeakerState, setSelectedSpeakerState] = useState<SpeakerState>(allowSpeakerTalkdown ? SpeakerState.Menu : SpeakerState.QuickSounds);
    const [isUnitMuteProcessing, setIsUnitMuteProcessing] = useState(false);

    const dispatch = useDispatch();

    const meta = useSelector((state) => unwrap(state.stream.streamMeta));
    const speakerSounds = meta?.capabilities.speakerSounds;
    const cameraId = meta?.cameraId;

    const actionButtonClasses = useActionButtonStyles();
    const classes = useStyles();

    const controlService = controlServiceImpl(client, cameraId!); // cameraId should never be null

    const [startSpeakerTalkdown, startSpeakerTalkdownState] = useLazySpeakerTalkdownStartQuery(undefined);

    const handleSelectedSpeakerState = (selection: SpeakerState) => {
        setSelectedSpeakerState(selection);
    };


    // Callbacks passed to the SIP handler
    const callConnected = useCallback(() => {
        setIsCallConnected(true);
        setIsCallConnecting(false);
    }, []);
    const callStoppedFailure = useCallback((error: string) => {
        console.log("Call Error", error);
        setIsCallConnected(false);
        setCallControls(null);
        setSelfMuted(false);
        setUnitMuted(true);

        dispatch(
            addAlert({
                severity: Severity.Error,
                message: `Call failed: ${error}`,
            }),
        );

    }, [startSpeakerTalkdownState]);
    const callStoppedSuccess = useCallback(() => {
        setIsCallConnected(false);
        setCallControls(null);
        setSelfMuted(false);
        setUnitMuted(true);
    }, [startSpeakerTalkdownState]);


    // Utilizing Speaker Talkdown endpoints
    const intiateTalkdown = () => {
        startSpeakerTalkdown(liveUnitId);
        setIsCallConnecting(true);
    };

    useEffect(() => {
        const { data, isSuccess, isFetching } = startSpeakerTalkdownState;

        if (data && isSuccess && !isFetching) {
            const config: Config = {
                token: data.data.token,
                lvtLiveUnitId: data.data.lvt_live_unit_id,
                callConnected,
                callStoppedFailure,
                callStoppedSuccess,
            };

            // The SIP handler passes up some functions to control the call, saved as callControls
            const callControls = startTwilioCall(config);

            setCallControls(callControls);
        }
    }, [startSpeakerTalkdownState]);

    // Utilizing callControls
    const hangupCall = () => {
        if (callControls) {
            callControls.hangup();
        }
    };
    const muteSelf = () => {

        if (callControls) {
            callControls.muteLocal();
            setSelfMuted(true);
        }
    };
    const unmuteSelf = () => {
        if (callControls) {
            callControls.unmuteLocal();
            setSelfMuted(false);
        }
    };


    const handleUnitMute = async () => {
        setIsUnitMuteProcessing(true);

        const newState = unitMuted ? ToggleMic.Unmute : ToggleMic.Mute;

        if (cameraId) {
            try {
                await controlService.speakerMic(newState);

                newState === ToggleMic.Mute ? setUnitMuted(true) : setUnitMuted(false);
            } catch (e) {
                const muteState = newState === ToggleMic.Mute ? "mute" : "unmute";

                dispatch(
                    addAlert({
                        severity: Severity.Error,
                        message: `Failed to ${muteState} speaker: ${e}`,
                    }),
                );
            } finally {
                setIsUnitMuteProcessing(false);
            }
        }
    };

    const unitMuteIcon = isUnitMuteProcessing ? <CircularProgress size={24} color="secondary" /> : (unitMuted ? <VolumeOff /> : <VolumeUp />);

    const talkdownContent = (
        <>
            <List>
                <ListItem>
                    <ListItemIcon className={classes.phoneNumberIcon}>
                        <Smartphone classes={{ root: classes.icon }} />
                    </ListItemIcon>
                    <ListItemText
                        primary={<Typography variant="body2">{meta?.sipPhoneNumber}</Typography>}
                        secondary={<Typography variant="body2">EXT: {meta?.sipPin} | Pass: {meta?.clientPasscode}</Typography>}
                    />
                </ListItem>
            </List>
            <Grid container spacing={2} className={classes.buttonContainer}>
                {isCallConnected ? (
                    <>
                        <Grid item>
                            <Button
                                variant="contained"
                                startIcon={<Phone />}
                                className={clsx(classes.speakerTalkdownButton, {
                                    [classes.redButton]: isCallConnected,
                                })}
                                onClick={isCallConnected ? hangupCall : intiateTalkdown}
                                size="small"
                            >
                                End Call
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="outlined"
                                className={classes.speakerTalkdownIconButton}
                                onClick={selfMuted ? unmuteSelf : muteSelf}
                                size="small"
                            >
                                {selfMuted ? <MicOff /> : <Mic />}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="outlined"
                                className={classes.speakerTalkdownIconButton}
                                onClick={handleUnitMute}
                                size="small"
                            >
                                {unitMuteIcon}
                            </Button>
                        </Grid>
                    </>
                ) : (<Grid item>
                    <Button
                        id={"streaming_speaker-button_call-unit"}
                        variant="text"
                        startIcon={isCallConnecting ? <CircularProgress size={16} color="secondary" /> : <Phone />}
                        className={classes.speakerTalkdownButton}
                        onClick={isCallConnecting ? () => { } : intiateTalkdown}
                        size="small"
                    >
                        Call Unit
                    </Button>
                </Grid>)}
            </Grid>
        </>
    );

    let content: JSX.Element;

    switch (selectedSpeakerState) {
        case SpeakerState.Menu:
            if (speakerSounds && speakerSounds?.length && cameraId) {
                content = (
                    <>
                        <IconButton aria-label="close" onClick={() => handleMenu(OpenButton.None)} classes={{ root: classes.close }}>
                            <Close />
                        </IconButton>
                        <CardHeader title="Audio Options" />
                        <Divider className={classes.divider} />
                        <List>

                            <ListItem
                                id={"streaming_speaker-button_speaker"}
                                onClick={() => { handleSelectedSpeakerState(SpeakerState.TalkDown); }}
                                button
                            >
                                <ListItemIcon >
                                    <Speaker classes={{ root: classes.icon }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={<Typography variant="body1">Speaker</Typography>}
                                />
                                <ListItemSecondaryAction>
                                    <ArrowForwardIos />
                                </ListItemSecondaryAction>
                            </ListItem>
                            <ListItem
                                id={"streaming_speaker-button_play-sound"}
                                onClick={() => { handleSelectedSpeakerState(SpeakerState.QuickSounds); }}
                                button
                            >
                                <ListItemIcon >
                                    <MusicNote classes={{ root: classes.icon }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={<Typography variant="body1">Play Sound</Typography>}
                                />
                                <ListItemSecondaryAction>
                                    <ArrowForwardIos />
                                </ListItemSecondaryAction>
                            </ListItem>
                        </List>
                    </>
                );
            } else {
                content = (
                    <>
                        <IconButton aria-label="close" onClick={() => handleMenu(OpenButton.None)} classes={{ root: classes.close }}>
                            <Close />
                        </IconButton>
                        <CardHeader title="Call Unit" />
                        <Divider className={classes.divider} />

                        {talkdownContent}
                    </>
                );
            }

            break;
        case SpeakerState.QuickSounds:
            if (speakerSounds && speakerSounds?.length && cameraId)
                content = (
                    <>
                        <IconButton
                            aria-label="close"
                            onClick={() => handleMenu(OpenButton.None)}
                            className={classes.close}
                        >
                            <Close />
                        </IconButton>
                        {allowSpeakerTalkdown ?
                            <div style={{ display: "flex" }}>
                                <IconButton
                                    disabled={isCallConnected || isCallConnecting}
                                    onClick={() => handleSelectedSpeakerState(SpeakerState.Menu)}
                                    classes={{ root: classes.back }}
                                >
                                    <KeyboardArrowLeft />
                                </IconButton>
                                <CardHeader title="Play Sound" classes={{ root: classes.headerWithArrow }} />
                            </div>
                            :
                            <CardHeader title="Play Sound" />
                        }
                        <Divider className={classes.divider} />
                        <QuickSounds
                            sounds={speakerSounds}
                            cameraId={cameraId}
                            onError={() => {
                                // TODO: Put back when edge stops returning errors every time
                                // dispatch(
                                //     addAlert({
                                //         severity: Severity.Error,
                                //         message: e,
                                //     }),
                                // )
                            }}
                        />
                    </>
                );

            break;
        case SpeakerState.TalkDown:
            content = (
                <>
                    <IconButton aria-label="close" onClick={() => handleMenu(OpenButton.None)} classes={{ root: classes.close }}>
                        <Close />
                    </IconButton>
                    <div style={{ display: "flex" }}>
                        <IconButton
                            disabled={isCallConnected || isCallConnecting}
                            onClick={() => handleSelectedSpeakerState(SpeakerState.Menu)}
                            classes={{ root: classes.back }}
                        >
                            <KeyboardArrowLeft />
                        </IconButton>
                        <CardHeader title="Call Unit" classes={{ root: classes.headerWithArrow }} />
                    </div>
                    <Divider className={classes.divider} />
                    {talkdownContent}
                </>
            );
            break;

        default:
            break;
    }

    const id = open ? "popper" : undefined;

    return (
        <>
            <Popper
                id={id}
                open={open}
                disablePortal
                anchorEl={menuAnchor}
                transition
                placement="top-end"
                className={classes.popper}
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350}>
                        <Card raised classes={{ root: classes.menu }}>

                            <div className={classes.cardContent}>
                                {content}
                            </div>
                        </Card>
                    </Fade>
                )}
            </Popper>
            <Grid item className={actionButtonClasses.fabItem}>
                <Tooltip
                    PopperProps={{ disablePortal: true }}
                    open={showTooltip}
                    disableHoverListener
                    onMouseEnter={() => !open && setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    title={allowSpeakerTalkdown ? "Audio Options" : "Quick Sounds"}
                    placement="top"
                >
                    <Fab
                        onClick={() => handleMenu(OpenButton.SpeakerTalkDown)}
                        className={clsx(actionButtonClasses.fab, { [classes.redButton]: isCallConnected }, open && actionButtonClasses.active)}
                    >
                        <VolumeUp />
                    </Fab>
                </Tooltip>
            </Grid>
        </>
    );
};

export default SpeakerButtons;
